// src/store/slices/review.slice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { reviewController } from '../../controllers/review.controller'

/* ---------------------------------------------------------- */
/* ------------------ Async Thunks -------------------------- */
/* ---------------------------------------------------------- */

// שליפת כל הביקורות לפי פילטר (למשל animeId)
export const fetchReviews = createAsyncThunk(
  'review/fetchReviews',
  async (filter, { rejectWithValue }) => {
    try {
      return await reviewController.fetchReviews(filter)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// שליפת ביקורת בודדת לפי ID
export const fetchReviewById = createAsyncThunk(
  'review/fetchReviewById',
  async (reviewId, { rejectWithValue }) => {
    try {
      return await reviewController.fetchReviewById(reviewId)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// הוספת ביקורת חדשה
export const addReview = createAsyncThunk(
  'review/addReview',
  async ({ reviewData, loggedInUser }, { rejectWithValue }) => {
    try {
      return await reviewController.addReview(reviewData, loggedInUser)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// עריכת ביקורת קיימת
export const editReview = createAsyncThunk(
  'review/editReview',
  async ({ reviewId, updatedData, loggedInUser }, { rejectWithValue }) => {
    try {
      return await reviewController.editReview(
        reviewId,
        updatedData,
        loggedInUser
      )
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// מחיקת ביקורת
export const removeReview = createAsyncThunk(
  'review/removeReview',
  async ({ reviewId, loggedInUser }, { rejectWithValue }) => {
    try {
      await reviewController.removeReview(reviewId, loggedInUser)
      return reviewId
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

/* ---------------------------------------------------------- */
/* ---------------------- Slice ----------------------------- */
/* ---------------------------------------------------------- */

const initialState = {
  reviews: [],
  isLoading: false,
  error: null,
}

const reviewSlice = createSlice({
  name: 'review',
  initialState,
  reducers: {
    addReviewDirect(state, action) {
      state.reviews.push(action.payload)
    },
    updateReviewDirect(state, action) {
      const idx = state.reviews.findIndex(
        (review) => review._id === action.payload._id
      )
      if (idx !== -1) {
        state.reviews[idx] = action.payload
      }
    },
    removeReviewDirect(state, action) {
      state.reviews = state.reviews.filter(
        (review) => review._id !== action.payload
      )
    },
  },
  extraReducers: (builder) => {
    builder
      /* 🔹 שליפת כל הביקורות */
      .addCase(fetchReviews.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchReviews.fulfilled, (state, action) => {
        state.isLoading = false
        state.reviews = action.payload
      })
      .addCase(fetchReviews.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      /* 🔹 שליפת ביקורת בודדת */
      .addCase(fetchReviewById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchReviewById.fulfilled, (state, action) => {
        state.isLoading = false
        const idx = state.reviews.findIndex(
          (review) => review._id === action.payload._id
        )
        if (idx !== -1) {
          state.reviews[idx] = action.payload
        } else {
          state.reviews.push(action.payload)
        }
      })
      .addCase(fetchReviewById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      /* 🔹 הוספת ביקורת */
      .addCase(addReview.fulfilled, (state, action) => {
        const newReview = action.payload
        const exists = state.reviews.some(
          (review) => review._id === newReview._id
        )
        if (!exists) {
          state.reviews.push(newReview)
        }
      })

      /* 🔹 עריכת ביקורת */
      .addCase(editReview.fulfilled, (state, action) => {
        const idx = state.reviews.findIndex(
          (review) => review._id === action.payload._id
        )
        if (idx !== -1) {
          state.reviews[idx] = action.payload
        }
      })

      /* 🔹 מחיקת ביקורת */
      .addCase(removeReview.fulfilled, (state, action) => {
        state.reviews = state.reviews.filter(
          (review) => review._id !== action.payload
        )
      })
  },
})

/* ---------------------------------------------------------- */
/* -------------------- Exports ----------------------------- */
/* ---------------------------------------------------------- */

export const { addReviewDirect, updateReviewDirect, removeReviewDirect } =
  reviewSlice.actions

export const reviewReducer = reviewSlice.reducer
